import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { dealerAccountApi } from '../services/api';

/**
 * Surface the dealer's account-state flags (specifically the new-orders block)
 * to the entire dealer portal so we can gate write surfaces with a popup.
 *
 * When `newOrdersBlocked` is true, the only feature the dealer can use is
 * paying invoices on existing orders. Everything else (New Order, Parts,
 * Quote Calculator, Price Builder, Screen Designer, etc.) shows a popup
 * directing them to clear their balance first.
 */
interface AccountStatus {
  newOrdersBlocked: boolean;
  newOrdersBlockReason: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AccountStatus>({
  newOrdersBlocked: false,
  newOrdersBlockReason: null,
  loading: true,
  refresh: async () => {},
});

export function AccountStatusProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [state, setState] = useState({
    newOrdersBlocked: false,
    newOrdersBlockReason: null as string | null,
    loading: true,
  });

  const refresh = useCallback(async () => {
    // Only dealer users have a dealerId-scoped status endpoint.
    if (!isAuthenticated || !(user as any)?.dealerId) {
      setState({ newOrdersBlocked: false, newOrdersBlockReason: null, loading: false });
      return;
    }
    try {
      const s = await dealerAccountApi.getMyAccountStatus();
      setState({
        newOrdersBlocked: !!s.newOrdersBlocked,
        newOrdersBlockReason: s.newOrdersBlockReason,
        loading: false,
      });
    } catch {
      // Don't lock dealers out on a transient error — fall safe to "not blocked".
      setState({ newOrdersBlocked: false, newOrdersBlockReason: null, loading: false });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Ctx.Provider value={{ ...state, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAccountStatus() {
  return useContext(Ctx);
}
